import { useState, useEffect, useCallback, useRef } from 'react'

const INTRO_KEY = 'ws_brochure_intro_seen'

interface IntroSentence {
  text: string
  highlight: string
}

export type IntroVariant = 'adviser' | 'dfm' | 'mortgage'

const adviserSentences: IntroSentence[] = [
  { text: 'The ', highlight: 'Adviser Operating System' },
  { text: 'More ', highlight: 'profit' },
  { text: 'Less ', highlight: 'admin' },
  { text: 'Work you ', highlight: 'enjoy' },
  { text: "Integrates with ", highlight: "what you've got" },
  { text: 'Or becomes ', highlight: 'everything you need' },
  { text: 'Built by advisers. ', highlight: 'For advisers' },
  { text: 'The future of advice ', highlight: 'starts here' },
  { text: 'Welcome to ', highlight: 'WealthSpace' },
]

const dfmSentences: IntroSentence[] = [
  { text: 'More ', highlight: 'profit' },
  { text: 'Less ', highlight: 'admin' },
  { text: 'Work you ', highlight: 'enjoy' },
  { text: 'Built by financial professionals. ', highlight: 'For financial professionals' },
  { text: 'The future of investment management ', highlight: 'starts here' },
  { text: 'Welcome to ', highlight: 'WealthSpace' },
]

const mortgageSentences: IntroSentence[] = [
  { text: 'The ', highlight: 'Mortgage Adviser Operating System' },
  { text: 'More ', highlight: 'profit' },
  { text: 'Less ', highlight: 'admin' },
  { text: 'Work you ', highlight: 'enjoy' },
  { text: 'From consultation to ', highlight: 'completion' },
  { text: 'Built by advisers. ', highlight: 'For mortgage advisers' },
  { text: 'The future of mortgage advice ', highlight: 'starts here' },
  { text: 'Welcome to ', highlight: 'WealthSpace' },
]

const sentencesByVariant: Record<IntroVariant, IntroSentence[]> = {
  adviser: adviserSentences,
  dfm: dfmSentences,
  mortgage: mortgageSentences,
}

const highlightColorByVariant: Record<IntroVariant, string> = {
  adviser: '#14b8a6',
  dfm: '#f59e0b',
  mortgage: '#3b82f6',
}

function isInternalNavigation(): boolean {
  if (typeof window === 'undefined') return false
  const referrer = document.referrer
  if (!referrer) return false
  try {
    const referrerUrl = new URL(referrer)
    return referrerUrl.origin === window.location.origin
  } catch {
    return false
  }
}

function shouldShowIntro(): boolean {
  if (typeof window === 'undefined') return false
  if (sessionStorage.getItem(INTRO_KEY) || isInternalNavigation()) {
    sessionStorage.setItem(INTRO_KEY, 'true')
    return false
  }
  return true
}

interface IntroAnimationProps {
  variant?: IntroVariant
  /** Called when the animation completes - fires BEFORE the overlay fades so the parent can navigate immediately */
  onComplete?: () => void
  autoManage?: boolean
}

export default function IntroAnimation({
  variant = 'adviser',
  onComplete,
  autoManage = false,
}: IntroAnimationProps) {
  const [isVisible, setIsVisible] = useState(() => (autoManage ? shouldShowIntro() : true))
  const [isFadingOut, setIsFadingOut] = useState(false)
  const [displayIndex, setDisplayIndex] = useState(0)
  const [textState, setTextState] = useState<'visible' | 'fading' | 'hidden'>('hidden')
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const indexRef = useRef(0)
  const finishedRef = useRef(false)

  const introSentences = sentencesByVariant[variant]
  const highlightColor = highlightColorByVariant[variant]

  const finishIntro = useCallback(() => {
    if (finishedRef.current) return
    finishedRef.current = true

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    if (autoManage) {
      sessionStorage.setItem(INTRO_KEY, 'true')
    }

    // Navigate FIRST (while overlay is still fully visible), then fade out
    onComplete?.()

    setIsFadingOut(true)
    setTimeout(() => {
      setIsVisible(false)
    }, 800)
  }, [autoManage, onComplete])

  useEffect(() => {
    if (!isVisible) return

    const totalSentences = introSentences.length

    const showNextSentence = () => {
      if (indexRef.current >= totalSentences) {
        finishIntro()
        return
      }

      setTextState('fading')

      timeoutRef.current = setTimeout(() => {
        indexRef.current += 1
        setDisplayIndex(indexRef.current)
        setTextState('visible')

        timeoutRef.current = setTimeout(showNextSentence, 2000)
      }, 400)
    }

    timeoutRef.current = setTimeout(showNextSentence, 500)

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [isVisible]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!isVisible) return null

  const currentSentence = introSentences[Math.max(0, displayIndex - 1)]

  return (
    <div
      className={`
        fixed inset-0 bg-[#0a0d12] flex items-center justify-center z-[9999] flex-col
        transition-opacity duration-[800ms] ease-out
        ${isFadingOut ? 'opacity-0 pointer-events-none' : 'opacity-100'}
      `}
    >
      {/* Intro Text */}
      <div
        className={`
          font-serif text-2xl sm:text-4xl text-brand-light text-center max-w-[700px] px-6 leading-[1.4]
          transition-all duration-[800ms] ease-out
          ${textState === 'visible' ? 'opacity-100 translate-y-0' : ''}
          ${textState === 'fading' ? 'opacity-0 -translate-y-5' : ''}
          ${textState === 'hidden' ? 'opacity-0 translate-y-5' : ''}
        `}
      >
        {displayIndex > 0 && currentSentence && (
          <>
            {currentSentence.text}
            <span className="inline" style={{ color: highlightColor }}>
              {currentSentence.highlight}
            </span>
            .
          </>
        )}
      </div>

      {/* Progress Dots */}
      <div className="absolute bottom-[60px] flex gap-2">
        {introSentences.map((_, index) => (
          <div
            key={index}
            className="w-2 h-2 rounded-full transition-all duration-300 ease-out"
            style={{
              background: index === displayIndex - 1 || (displayIndex === 0 && index === 0)
                ? highlightColor
                : 'rgba(255,255,255,0.2)',
              boxShadow: index === displayIndex - 1 || (displayIndex === 0 && index === 0)
                ? `0 0 12px ${highlightColor}80`
                : 'none'
            }}
          />
        ))}
      </div>

      {/* Skip Button */}
      <button
        onClick={finishIntro}
        className="
          absolute bottom-[100px] bg-transparent border border-white/20 text-brand-muted
          px-6 py-2.5 rounded-md text-sm font-sans cursor-pointer
          transition-all duration-300 ease-out
          hover:border-white/40 hover:text-brand-light hover:bg-white/5
        "
      >
        Skip
      </button>
    </div>
  )
}
